import React from 'react';
import { Card, CardContent } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Building2, CheckCircle, XCircle, Users, Link as LinkIcon, Edit2, Trash2 } from 'lucide-react';
import { cn } from '../../ui/utils';

// Simplified interface for display
interface CompanySummary {
    id: string;
    companyName: string;
    subdomain: string;
    contactEmail: string;
    contactName: string;
    phone?: string;
    primaryColor?: string;
    logo?: string;
    wellCount?: number;
    userCount?: number;
    status: 'active' | 'suspended';
    createdAt: string;
}

interface CompanyListProps {
    companies: CompanySummary[];
    onEdit: (company: CompanySummary) => void;
    onViewUsers: (companyId: string) => void;
    onDelete?: (companyId: string) => void;
    isLoading: boolean;
}

export function CompanyList({ companies, onEdit, onViewUsers, onDelete, isLoading }: CompanyListProps) {
    if (isLoading) {
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3, 4, 5, 6].map(i => (
                    <Card key={i} className="animate-pulse">
                        <CardContent className="h-40 bg-gray-100 rounded-lg p-6">
                            <div className="h-10 w-10 bg-gray-200 rounded-full mb-4"></div>
                            <div className="h-4 w-3/4 bg-gray-200 rounded mb-2"></div>
                            <div className="h-3 w-1/2 bg-gray-200 rounded"></div>
                        </CardContent>
                    </Card>
                ))}
            </div>
        );
    }

    if (companies.length === 0) {
        return (
            <div className="text-center py-12 bg-white rounded-lg border border-dashed text-muted-foreground">
                <Building2 className="w-12 h-12 mx-auto mb-3 opacity-20" />
                <p>No companies found</p>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {companies.map(company => (
                <Card key={company.id} className="group hover:shadow-md transition-shadow relative overflow-hidden">
                    {/* Status Stripe */}
                    <div className={cn(
                        "absolute top-0 left-0 w-1 h-full",
                        company.status === 'active' ? "bg-green-500" : "bg-red-500"
                    )} />

                    <CardContent className="p-5 pl-7">
                        <div className="flex justify-between items-start mb-4">
                            <div className="flex items-center gap-3">
                                <div
                                    className="w-10 h-10 rounded-lg flex items-center justify-center text-white shadow-sm font-bold text-lg"
                                    style={{ backgroundColor: company.primaryColor || '#3B82F6' }}
                                >
                                    {company.logo ? (
                                        <img src={company.logo} alt="" className="w-full h-full object-cover rounded-lg" />
                                    ) : (
                                        company.companyName.charAt(0)
                                    )}
                                </div>
                                <div className="space-y-0.5">
                                    <h3 className="font-semibold text-lg leading-tight line-clamp-1" title={company.companyName}>
                                        {company.companyName}
                                    </h3>
                                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                        <LinkIcon className="w-3 h-3" />
                                        <span>{company.subdomain}</span>
                                    </div>
                                </div>
                            </div>
                            {company.status === 'active' ? (
                                <Badge variant="outline" className="text-green-600 bg-green-50 border-green-200">
                                    Active
                                </Badge>
                            ) : (
                                <Badge variant="outline" className="text-red-600 bg-red-50 border-red-200">
                                    Suspended
                                </Badge>
                            )}
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                            <div className="p-2 bg-slate-50 rounded-lg text-center">
                                <div className="text-muted-foreground text-xs mb-0.5">Contact</div>
                                <div className="font-medium truncate" title={company.contactName}>{company.contactName}</div>
                            </div>
                            <div className="p-2 bg-slate-50 rounded-lg text-center">
                                <div className="text-muted-foreground text-xs mb-0.5">Users / Wells</div>
                                <div className="font-medium">
                                    {company.userCount || 0} <span className="text-muted-foreground mx-1">/</span> {company.wellCount || 0}
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-2 pt-2 border-t">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="flex-1 text-xs"
                                onClick={() => onViewUsers(company.id)}
                            >
                                <Users className="w-3.5 h-3.5 mr-2" />
                                Manage Users
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-xs px-2"
                                onClick={() => onEdit(company)}
                            >
                                <Edit2 className="w-3.5 h-3.5" />
                            </Button>
                            {onDelete && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-xs px-2 text-red-500 hover:text-red-600 hover:bg-red-50"
                                    onClick={() => onDelete(company.id)}
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                            )}
                        </div>
                    </CardContent>
                </Card>
            ))}
        </div>
    );
}
